import { useContext, useEffect, useMemo, useRef, useState } from "react"
import {
    Alert,
    Box,
    Button,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { Repeat } from "lib/@components/repeat"
import { OkCancelContext } from "lib/@hooks/useDialog"
import { setFromEvent } from "lib/setFromEvent"
import {
    getMaintenanceCategories,
    getMaintenanceCategory,
    getMaintenanceTask,
    getMaintenanceTaskElement,
    getMaintenanceTaskElementOptions,
    getMaintenanceTasks,
    resolveMaintenanceTaskElement,
} from "routes/component-library/controller/maintenance-library"
import { MaintenanceTaskElementSelectorBreadcrumbItem } from "./maintenance-task-element-selector-breadcrumb-item"
import { MaintenanceTaskElementSelectorCategoryItem } from "./maintenance-task-element-selector-category-item"
import { MaintenanceTaskElementSelectorTaskItem } from "./maintenance-task-element-selector-task-item"
import { MaintenanceTaskElementSelectorElementItem } from "./maintenance-task-element-selector-element-item"

const ROOT_CATEGORY_ID = "components"
const DEFAULT_ELEMENT_KEY = "actionsHtml"

export function MaintenanceTaskElementSelectorDialog({
    value,
    onApply,
    onCancel,
    showDialogFrame = true,
    title = "Select Maintenance Library Content",
    applyCaption = "Select",
    cancelCaption = "Cancel",
    excludedValueTypes = [],
    taskOnly = false,
    filterTasks,
    taskFilters,
}) {
    const searchInputRef = useRef(null)
    const [search, setSearch] = useState("")
    const [categoryId, setCategoryId] = useState(ROOT_CATEGORY_ID)
    const [categoryHistory, setCategoryHistory] = useState([])
    const [pendingCategory, setPendingCategory] = useState(null)
    const [selectedTaskId, setSelectedTaskId] = useState("")
    const [selectedElement, setSelectedElement] = useState(DEFAULT_ELEMENT_KEY)

    const resolvedReference = resolveMaintenanceTaskElement.useResults(value || undefined)
    const resolvedReferenceId = resolvedReference?.id
    const resolvedReferenceCategoryId = resolvedReference?.categoryId
    const resolvedReferenceTaskId = resolvedReference?.taskId
    const resolvedReferenceElement = resolvedReference?.element
    const category = getMaintenanceCategory.useResults(categoryId, true)
    const childCategories = getMaintenanceCategories.useResults({ parentId: categoryId })
    const rawElementOptions = getMaintenanceTaskElementOptions.useResults()
    const elementOptions = useMemo(getElementOptions, [rawElementOptions])
    const excludedValueTypeSet = useMemo(createExcludedValueTypeSet, [excludedValueTypes])
    const visibleElementOptions = useMemo(getVisibleElementOptions, [elementOptions, excludedValueTypeSet])
    const selectedTask = getMaintenanceTask.useResults(selectedTaskId || undefined)
    const searchSettings = useMemo(createSearchSettings, [search, categoryId, taskFilters])
    const searchedTasks = getMaintenanceTasks.useResults(searchSettings)?.items
    const visibleTasks = useMemo(getVisibleTasks, [search, searchedTasks, category?.tasks, filterTasks])
    const referenceQuery = useMemo(createReferenceQuery, [selectedTaskId, selectedElement])
    const selectedReference = getMaintenanceTaskElement.useResults(referenceQuery)
    const parentCategoryId = useMemo(getParentCategoryId, [category?.parentId])
    const sortedCategories = useMemo(sortCategories, [childCategories])
    const isSearching = !!search.trim()
    const showCategories = !isSearching && sortedCategories.length > 0
    const showElementColumn = !taskOnly && !!selectedTaskId
    const { ok: dialogOk, cancel: dialogCancel } = useContext(OkCancelContext)

    useEffect(focusSearchOnMount, [])
    useEffect(applyCurrentValueSelection, [
        value,
        resolvedReferenceId,
        resolvedReferenceCategoryId,
        resolvedReferenceTaskId,
        resolvedReferenceElement,
    ])
    useEffect(ensureSelectedElementIsVisible, [selectedElement, visibleElementOptions])

    return (
        <If value={showDialogFrame}>
            <Stack sx={{ minHeight: 680 }}>
                <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>{renderSelectorBody()}</DialogContent>
                {renderEmbeddedActions()}
            </Stack>
            <Else>
                <Stack
                    sx={{
                        width: 1080,
                        maxWidth: "calc(100vw - 48px)",
                        p: 2,
                        pt: 1.5,
                        maxHeight: "calc(100vh - 160px)",
                    }}
                >
                    {renderSelectorBody()}
                    {renderEmbeddedActions()}
                </Stack>
            </Else>
        </If>
    )

    function focusSearchOnMount() {
        const id = requestAnimationFrame(() => {
            searchInputRef.current?.focus()
            searchInputRef.current?.select()
        })
        return () => cancelAnimationFrame(id)
    }

    function createSearchSettings() {
        const text = search.trim()
        if (!text) return undefined

        return {
            search: text,
            categoryId,
            skip: 0,
            take: 100,
            orderStmt: ["heading"],
            hasClassification: Boolean(taskFilters?.hasClassification),
            hasFrequency: Boolean(taskFilters?.hasFrequency),
            hasSkillSet: Boolean(taskFilters?.hasSkillSet),
        }
    }

    function getVisibleTasks() {
        const tasks = search.trim() ? searchedTasks || [] : category?.tasks || []
        if (filterTasks) return tasks.filter(filterTasks)
        return tasks
    }

    function createReferenceQuery() {
        if (!selectedTaskId || !selectedElement) return undefined

        return {
            taskId: selectedTaskId,
            element: selectedElement,
        }
    }

    function sortCategories() {
        if (!childCategories) return []
        return [...childCategories].sort(sortCategoryItems)
    }

    function createExcludedValueTypeSet() {
        return new Set(excludedValueTypes.map(normaliseValueType).filter(Boolean))
    }

    function getElementOptions() {
        return rawElementOptions || []
    }

    function getVisibleElementOptions() {
        if (!excludedValueTypeSet.size) return elementOptions
        return elementOptions.filter(filterVisibleElementOption)

        function filterVisibleElementOption(option) {
            return !excludedValueTypeSet.has(normaliseValueType(option?.valueType))
        }
    }

    function applyCurrentValueSelection() {
        if (!value || !resolvedReferenceId) return

        setCategoryHistory([])
        setPendingCategory(null)
        setCategoryId(resolvedReferenceCategoryId || ROOT_CATEGORY_ID)
        setSelectedTaskId(resolvedReferenceTaskId || "")
        setSelectedElement(resolvedReferenceElement || DEFAULT_ELEMENT_KEY)
    }

    function ensureSelectedElementIsVisible() {
        if (!visibleElementOptions.length) {
            if (selectedElement) setSelectedElement("")
            return
        }
        if (hasElementOptionKey(visibleElementOptions, selectedElement)) return
        setSelectedElement(visibleElementOptions[0]?.key || "")
    }

    function getParentCategoryId() {
        return normaliseCategoryId(category?.parentId)
    }

    function openParentCategory() {
        if (categoryHistory.length > 0) {
            const prev = categoryHistory[categoryHistory.length - 1]
            setCategoryHistory((h) => h.slice(0, -1))
            setCategoryId(prev.id)
            setPendingCategory({ name: prev.name, type: prev.type })
        } else if (parentCategoryId) {
            setCategoryId(parentCategoryId)
            setPendingCategory(null)
        }
        clearSelection()
    }

    function handleCategoryChange(newCategoryId) {
        if (categoryId !== ROOT_CATEGORY_ID) {
            setCategoryHistory((prev) => [...prev, { id: categoryId, name: category?.name, type: category?.type }])
        }
        const target = sortedCategories.find((c) => c.id === newCategoryId)
        setPendingCategory(target ? { name: target.name, type: target.type } : null)
        setCategoryId(newCategoryId)
        clearSelection()
    }

    function navigateToRoot() {
        setCategoryHistory([])
        setPendingCategory(null)
        setCategoryId(ROOT_CATEGORY_ID)
        clearSelection()
    }

    function navigateToHistoryIndex(index) {
        const target = categoryHistory[index]
        setCategoryHistory((prev) => prev.slice(0, index))
        setPendingCategory({ name: target.name, type: target.type })
        setCategoryId(target.id)
        clearSelection()
    }

    function clearSelection() {
        setSelectedTaskId("")
    }

    function clearSearchText() {
        setSearch("")
    }

    function renderSelectorBody() {
        return (
            <Stack spacing={2}>
                <If value={resolvedReference?.id}>
                    <Alert severity="info">Current: {resolvedReference?.label}</Alert>
                </If>

                <Stack direction="row" spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        inputRef={searchInputRef}
                        value={search}
                        onChange={setFromEvent(setSearch)}
                        placeholder="Search components in this folder..."
                        data-cy="maintenance-task-element-selector-search"
                    />
                    <Button
                        data-cy="maintenance-selector-clear"
                        onClick={clearSearchText}
                        disabled={!search}
                        variant="outlined"
                        color="primary"
                    >
                        Clear
                    </Button>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: "hidden", minWidth: 0 }}>
                    <Tooltip title="Go to root">
                        <IconButton
                            data-cy="maintenance-selector-home"
                            size="small"
                            onClick={navigateToRoot}
                            sx={{ p: 0.25 }}
                        >
                            <Iconify icon="mdi:home-outline" width={16} />
                        </IconButton>
                    </Tooltip>
                    <Bound onBreadcrumbNavigate={navigateToHistoryIndex}>
                        <Repeat list={categoryHistory} item={<MaintenanceTaskElementSelectorBreadcrumbItem />} />
                    </Bound>
                    <If value={categoryId !== ROOT_CATEGORY_ID}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.disabled">
                                ›
                            </Typography>
                            <Iconify
                                icon={
                                    (pendingCategory ?? category)?.type === "directory"
                                        ? "mdi:folder-outline"
                                        : "mdi:file-document-outline"
                                }
                                width={14}
                                sx={{
                                    color:
                                        (pendingCategory ?? category)?.type === "directory"
                                            ? "warning.main"
                                            : "text.secondary",
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant="caption" color="text.primary" noWrap>
                                {(pendingCategory ?? category)?.name}
                            </Typography>
                        </Stack>
                    </If>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="stretch">
                    <Stack spacing={1} sx={{ width: 460, minWidth: 340 }}>
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">
                                <If value={showCategories}>
                                    Folders, Documents & Components<Else>Components</Else>
                                </If>
                            </Typography>
                            <Tooltip title="Up one level">
                                <span>
                                    <IconButton
                                        data-cy="maintenance-selector-up"
                                        aria-label="Up one level"
                                        size="small"
                                        color="primary"
                                        onClick={openParentCategory}
                                        disabled={!parentCategoryId}
                                    >
                                        <Iconify icon="mdi:arrow-up" width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                        <Box
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                maxHeight: 420,
                                overflow: "auto",
                            }}
                        >
                            <If value={showCategories || visibleTasks.length > 0}>
                                <List dense disablePadding data-cy="maintenance-selector-browse-list">
                                    <If value={showCategories}>
                                        <Bound categoryId={categoryId} setCategoryId={handleCategoryChange}>
                                            <Repeat
                                                list={sortedCategories}
                                                item={<MaintenanceTaskElementSelectorCategoryItem />}
                                            />
                                        </Bound>
                                    </If>
                                    <Bound
                                        selectedTaskId={selectedTaskId}
                                        setSelectedTaskId={setSelectedTaskId}
                                        showPath={isSearching}
                                        compact={!isSearching}
                                    >
                                        <Repeat list={visibleTasks} item={<MaintenanceTaskElementSelectorTaskItem />} />
                                    </Bound>
                                </List>
                                <Else>
                                    <Box p={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            No components found in this view.
                                        </Typography>
                                    </Box>
                                </Else>
                            </If>
                        </Box>
                    </Stack>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ visibility: showElementColumn ? "visible" : "hidden" }}
                    />
                    <Stack
                        spacing={1}
                        sx={{ width: 200, minWidth: 170, visibility: showElementColumn ? "visible" : "hidden" }}
                    >
                        <Typography variant="subtitle2">Content Element</Typography>
                        <Box
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                maxHeight: 420,
                                overflow: "auto",
                            }}
                        >
                            <If value={visibleElementOptions.length > 0}>
                                <Bound selectedElement={selectedElement} setSelectedElement={setSelectedElement}>
                                    <List dense disablePadding>
                                        <Repeat
                                            list={visibleElementOptions}
                                            item={<MaintenanceTaskElementSelectorElementItem />}
                                        />
                                    </List>
                                </Bound>
                                <Else>
                                    <Box p={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            No element options available.
                                        </Typography>
                                    </Box>
                                </Else>
                            </If>
                        </Box>
                    </Stack>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ visibility: showElementColumn ? "visible" : "hidden" }}
                    />
                    <Stack
                        spacing={1}
                        sx={{ flex: 1, minWidth: 180, visibility: showElementColumn ? "visible" : "hidden" }}
                    >
                        <Typography variant="subtitle2">Preview</Typography>
                        <Box
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                p: 1.5,
                                minHeight: 420,
                                maxHeight: 420,
                                overflow: "auto",
                            }}
                        >
                            <If value={selectedTask}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    data-cy="maintenance-selector-preview-type"
                                >
                                    Component
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5 }}>
                                    {selectedTask?.heading}
                                </Typography>
                            </If>

                            <If value={selectedTask?.componentInstructionsHtml}>
                                <Typography variant="caption" color="text.secondary">
                                    Component Instructions
                                </Typography>
                                <Box
                                    className="styled-bullets"
                                    sx={{
                                        typography: "caption",
                                        mb: 1.5,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1,
                                        bgcolor: "action.hover",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        wordBreak: "break-word",
                                        "& p": { mt: 0, mb: 0.5 },
                                        "& p:last-of-type": { mb: 0 },
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: selectedTask?.componentInstructionsHtml ?? "",
                                    }}
                                />
                            </If>

                            <If value={selectedReference?.id}>
                                <Typography variant="caption" color="text.secondary">
                                    Content
                                </Typography>
                                <If value={selectedReference?.valueType === "html"}>
                                    <Box
                                        className="styled-bullets"
                                        sx={{
                                            typography: "body2",
                                            wordBreak: "break-word",
                                            "& p": { mt: 0, mb: 1 },
                                            "& p:last-of-type": { mb: 0 },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: selectedReference?.valueText || "" }}
                                    />
                                    <Else>
                                        <Typography
                                            component="pre"
                                            variant="caption"
                                            sx={{
                                                m: 0,
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                fontFamily: "monospace",
                                            }}
                                        >
                                            {selectedReference?.valueText || "(Empty)"}
                                        </Typography>
                                    </Else>
                                </If>
                                <Else>
                                    <Typography variant="body2" color="text.secondary">
                                        Select a content element to preview.
                                    </Typography>
                                </Else>
                            </If>
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
        )
    }

    function renderEmbeddedActions() {
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, pb: 2 }}>
                <If value={taskOnly ? selectedTaskId : selectedReference?.id}>
                    <Alert severity="success" sx={{ flex: 1, py: 0, "& .MuiAlert-message": { fontSize: "0.75rem" } }}>
                        {taskOnly
                            ? selectedTask?.heading || selectedTaskId
                            : selectedReference?.label?.replace(/^Components\//, "")}
                    </Alert>
                    <Else>
                        <Box sx={{ flex: 1 }} />
                    </Else>
                </If>
                <Button data-cy="maintenance-selector-cancel" variant="outlined" color="primary" onClick={handleCancel}>
                    {cancelCaption}
                </Button>
                <Button
                    data-cy="maintenance-selector-insert"
                    variant="contained"
                    onClick={handleApply}
                    disabled={taskOnly ? !selectedTaskId : !selectedReference?.id}
                >
                    {applyCaption}
                </Button>
            </Stack>
        )
    }

    function handleApply() {
        if (taskOnly) {
            const result = { id: selectedTask?._id || selectedTaskId, label: selectedTask?.heading }
            if (onApply) {
                onApply(result?.id)
            } else {
                dialogOk?.(result)
            }
            return
        }
        if (onApply) {
            onApply(selectedReference?.id)
        } else {
            dialogOk?.(selectedReference)
        }
    }

    function handleCancel() {
        if (onCancel) {
            onCancel()
        } else {
            dialogCancel?.()
        }
    }
}

function sortCategoryItems(a, b) {
    const aType = a?.type || ""
    const bType = b?.type || ""
    if (aType !== bType) {
        if (aType === "directory") return -1
        if (bType === "directory") return 1
    }
    return (a?.name || "").localeCompare(b?.name || "")
}

function normaliseCategoryId(id) {
    if (!id) return null
    const text = `${id}`
    if (!text.includes(":")) return text
    return text.split(":")[0]
}

function normaliseValueType(valueType) {
    return `${valueType || ""}`.trim().toLowerCase()
}

function hasElementOptionKey(options, elementKey) {
    return options.some(matchesElementKey)

    function matchesElementKey(option) {
        return option?.key === elementKey
    }
}
